import type {
	AvailableAddon,
	BankGiroProduct,
	BaseAddon,
	CheckCreditProduct,
	CreditCardProduct,
	DebitCardProduct,
	GrowAddon,
	SavingAccountProduct,
	SelectedProduct,
	TransactionAccountProduct,
} from "./product";
import type { AmountAndCurrency } from "./shared";
import type { CreditScoreAssessment } from "./step";

type SharedPackageInfo = {
	baseMonthlyFee: AmountAndCurrency;
};

type BasePackageProduct =
	| TransactionAccountProduct
	| SavingAccountProduct
	| DebitCardProduct
	| BankGiroProduct;

type GrowPackageProduct =
	| TransactionAccountProduct
	| SavingAccountProduct
	| DebitCardProduct
	| CheckCreditProduct
	| CreditCardProduct
	| BankGiroProduct;

export type OnboardingPackage =
	| BasePackage
	| GrowPackage
	| OverDraftPackage
	| ForetagarnaPackage
	| ForetagarnaCreditPackage
	| EhandelPackage;

export type OnboardingPackageId = OnboardingPackage["packageId"];

export type ForetagarnaPackage = {
	packageId: "FÖRETAGARNA-BAS";
	name: "Företagarna";
	addons: BaseAddon[];
	products: BasePackageProduct[];
} & SharedPackageInfo;

export type ForetagarnaCreditPackage = {
	packageId: "FORETAGARNA-CARDONLY";
	name: "Företagarna Credit Card Only";
	addons: BaseAddon[];
	products: BasePackageProduct[];
} & SharedPackageInfo;

export type EhandelPackage = {
	packageId: "E-HANDEL-BAS";
	name: "E-handel Bas";
	addons: BaseAddon[];
	products: BasePackageProduct[];
} & SharedPackageInfo;

export type OverDraftPackage = {
	packageId: "OVERDRAFTPACKAGE";
	name: "Kontokrediten";
	addons: Array<
		(CreditCardProduct & { maxQuantity: number }) | (DebitCardProduct & { maxQuantity: number })
	>;
	products: Array<TransactionAccountProduct | CheckCreditProduct>;
} & SharedPackageInfo;

export type BasePackage = {
	packageId: "BAAS-FÖRETAG-BAS";
	name: "Företag bas";
	addons: BaseAddon[];
	products: BasePackageProduct[];
} & SharedPackageInfo;

export type GrowPackage = {
	packageId: "BAAS-FÖRETAG-VÄXA";
	name: "Företag växa";
	addons: GrowAddon[];
	products: GrowPackageProduct[];
} & SharedPackageInfo;

export type OnboardingPackageSet = [
	BasePackage,
	GrowPackage,
	OverDraftPackage,
	ForetagarnaPackage?,
	ForetagarnaCreditPackage?,
	EhandelPackage?,
];

export type ClcPackage = {
	packageId: string;
	name: string;
	baseMonthlyFee: AmountAndCurrency;
	products: ClcProduct[];
	addons: ClcProduct[];
};

export type ClcProduct = {
	definitionId: string;
	type: string;
	name: string;
	maxQuantity?: number;
	sku: string;
	config: Record<string, unknown>;
};

export type SelectedPackage = {
	assessment: CreditScoreAssessment | null;
	monthlyFee: AmountAndCurrency;
	packageId: OnboardingPackageId;
	products: SelectedProduct[];
	availableAddons: AvailableAddon[];
};

export type OnboardingClcPackageId = "SVEA-STARTA-BOLAG" | "FÖRETAGARNA-SVEA-STARTA-BOLAG";

export type SelectedClcPackage = {
	assessment: CreditScoreAssessment | null;
	monthlyFee: AmountAndCurrency;
	packageId: OnboardingClcPackageId;
	products: SelectedProduct[];
	availableAddons: AvailableAddon[];
};

export const isPackageOfType =
	<T extends OnboardingPackageId>(id: T) =>
	(p: OnboardingPackage): p is Extract<OnboardingPackage, { packageId: T }> =>
		p.packageId === id;

export type ChoosePackageError = { id: number; message: string };
